import path from "path";
import fs from "fs-extra";
import prettier from "prettier";

import { resolveAbsolutePath, toPosixPath } from "./utils";

export interface MockFileConfig {
  enable: boolean;
  data: unknown;
  delay: number;
  status: number;
  [key: string]: unknown;
}

export interface MockFileInfo {
  config: MockFileConfig;
  serializable: boolean;
  hasDynamicData: boolean;
  headerComment?: string;
  description?: string;
  dataText: string;
  isBinary?: boolean;
}

export const DEFAULT_CONFIG: MockFileConfig = {
  enable: true,
  data: null,
  delay: 0,
  status: 200,
};

const isBufferTextLike = (buffer: Buffer): boolean => {
  try {
    const sample = buffer.slice(0, 100);
    const nullBytes = [...sample].filter((b) => b === 0).length;
    const controlChars = [...sample].filter(
      (b) => b < 32 && b !== 9 && b !== 10 && b !== 13,
    ).length;
    return nullBytes === 0 && controlChars < 5;
  } catch {
    return false;
  }
};

const isBinaryResponse = (
  contentType: string | undefined,
  data: Buffer,
): boolean => {
  if (!contentType) return false;

  const binaryTypes = [
    "application/octet-stream",
    "application/pdf",
    "application/zip",
    "application/x-zip-compressed",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "image/",
    "video/",
    "audio/",
  ];

  return (
    binaryTypes.some((type) => contentType.toLowerCase().includes(type)) ||
    !isBufferTextLike(data)
  );
};

const getFileExtension = (
  contentType: string | undefined,
  url: string,
): string => {
  const mimeMap: Record<string, string> = {
    "application/json": "json",
    "application/pdf": "pdf",
    "application/zip": "zip",
    "application/vnd.ms-excel": "xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "docx",
    "application/vnd.ms-powerpoint": "ppt",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      "pptx",
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/gif": "gif",
    "text/plain": "txt",
    "text/html": "html",
    "text/css": "css",
    "application/javascript": "js",
    "text/xml": "xml",
  };

  if (contentType && mimeMap[contentType.toLowerCase()]) {
    return mimeMap[contentType.toLowerCase()];
  }

  try {
    const urlObj = new URL(url, "http://localhost");
    const fileName = urlObj.searchParams.get("file_name");
    if (fileName) {
      const extensionMatch = fileName.match(/\.([a-zA-Z0-9]+)$/);
      if (extensionMatch) {
        return extensionMatch[1];
      }
    }
  } catch {
    // Ignore URL parse errors
  }

  return "bin";
};

async function formatWithPrettier(content: string): Promise<string> {
  try {
    return await prettier.format(content, { parser: "babel" });
  } catch {
    return content;
  }
}

function buildMockFileHeader(
  pathname: string,
  method: string,
  search: string,
  extra?: string,
): string {
  return `/**
 * Mock data for ${pathname} (${method.toUpperCase()})${search || ""}
 * @description ${pathname}${search || ""}${extra ? ` - ${extra}` : ""}
 * Generated at ${new Date().toISOString()}
 */`;
}

async function saveBinaryMock(
  filePath: string,
  binaryData: Buffer,
  pathname: string,
  method: string,
  search: string,
  contentType: string | undefined,
  url: string,
  statusCode: number | undefined,
): Promise<string | null> {
  const extension = getFileExtension(contentType, url);
  const binaryFilePath = filePath.replace(/\.js$/, "." + extension);

  if (fs.existsSync(binaryFilePath)) return null;

  fs.writeFileSync(binaryFilePath, binaryData);

  const header = buildMockFileHeader(pathname, method, search, `Binary file (${extension})`);
  const configContent = `${header}
export default {
  enable: false,
  data: {
    __binaryFile: '${extension}',
    __originalPath: '${pathname}',
    __originalQuery: '${search}',
    __originalUrl: '${pathname}${search || ""}',
    __contentType: '${contentType}',
    __fileSize: ${binaryData.length}
  },
  delay: 0,
  status: ${statusCode || 200}
}`;

  const formatted = await formatWithPrettier(configContent);
  fs.writeFileSync(filePath, formatted, "utf-8");
  return filePath;
}

async function saveJsonMock(
  filePath: string,
  dataStr: string,
  pathname: string,
  method: string,
  search: string,
  statusCode: number | undefined,
): Promise<string | null> {
  if (fs.existsSync(filePath)) return null;

  let jsonData;
  if (!dataStr || dataStr.trim() === "") {
    jsonData = {
      error: true,
      message: `Empty response (${statusCode || "unknown status"})`,
      status: statusCode || 404,
      data: null,
    };
  } else {
    try {
      jsonData = JSON.parse(dataStr);
      if (statusCode && statusCode >= 400) {
        const errorMeta = { __mockStatusCode: statusCode, __isErrorResponse: true };
        jsonData =
          typeof jsonData === "object" && jsonData !== null
            ? { ...jsonData, ...errorMeta }
            : { originalData: jsonData, ...errorMeta };
      }
    } catch {
      jsonData = {
        error: true,
        message: `Non-JSON response (${statusCode || "unknown status"})`,
        status: statusCode || 404,
        data: dataStr,
        __mockStatusCode: statusCode,
        __isErrorResponse: true,
      };
    }
  }

  const header = buildMockFileHeader(pathname, method, search);
  const content = `${header}
export default {
  enable: false,
  data: ${JSON.stringify(jsonData)},
  delay: 0,
  status: ${statusCode || 200}
}`;

  const formatted = await formatWithPrettier(content);
  fs.writeFileSync(filePath, formatted, "utf-8");
  return filePath;
}

export async function saveMockData(
  url: string,
  method: string,
  data: Buffer | string,
  rootDir: string,
  statusCode?: number,
  contentType?: string,
): Promise<string | null> {
  try {
    const absoluteRootDir = resolveAbsolutePath(rootDir);
    const urlObj = new URL(url, "http://localhost");
    const pathname = urlObj.pathname;
    const search = urlObj.search;

    const filePath = path.join(
      absoluteRootDir,
      pathname.replace(/^\//, ""),
      method.toLowerCase() + ".js",
    );

    await fs.ensureDir(path.dirname(filePath));

    const isBuffer = Buffer.isBuffer(data);
    const binaryData = isBuffer ? data : Buffer.from(data || "");
    const isBinary = isBinaryResponse(contentType, binaryData);

    if (isBinary) {
      return saveBinaryMock(
        filePath, binaryData, pathname, method, search,
        contentType, url, statusCode,
      );
    }

    const dataStr = isBuffer ? data.toString("utf8") : data || "";
    return saveJsonMock(filePath, dataStr, pathname, method, search, statusCode);
  } catch (error) {
    console.error(`Failed to save mock data for ${url}:`, error);
    console.error(
      `URL details: url=${url}, method=${method}, statusCode=${statusCode}, contentType=${contentType}`,
    );
    throw error;
  }
}

const recursiveReadAllFiles = async (dir: string): Promise<string[]> => {
  if (!(await fs.pathExists(dir))) return [];

  const files: string[] = [];
  try {
    const list = await fs.readdir(dir);
    for (const file of list) {
      const filePath = path.join(dir, file);
      const stat = await fs.stat(filePath);
      if (stat.isDirectory()) {
        files.push(...(await recursiveReadAllFiles(filePath)));
      } else {
        files.push(filePath);
      }
    }
  } catch (error) {
    console.error(`Error reading directory ${dir}:`, error);
  }
  return files;
};

export async function buildMockIndex(mockDir: string): Promise<Map<string, string>> {
  const mockFileMap = new Map<string, string>();

  if (!(await fs.pathExists(mockDir))) {
    await fs.ensureDir(mockDir);
    return mockFileMap;
  }

  const files = await recursiveReadAllFiles(mockDir);

  files.forEach((filePath) => {
    if (!filePath.endsWith(".js")) return;

    try {
      const relativePath = path.relative(mockDir, filePath);
      const method = path.basename(filePath, ".js");
      const dirPath = path.dirname(relativePath);
      const urlPath = "/" + toPosixPath(dirPath);
      const absolutePath = resolveAbsolutePath(filePath);
      const key = `${urlPath}/${method}.js`.toLowerCase();

      mockFileMap.set(key, absolutePath);
    } catch (error) {
      console.error(`[automock] Failed to process file ${filePath}:`, error);
    }
  });

  return mockFileMap;
}

export async function parseMockModule(filePath: string): Promise<MockFileInfo> {
  const absolutePath = resolveAbsolutePath(filePath);

  if (!fs.existsSync(absolutePath)) {
    throw new Error(`Mock file does not exist: ${absolutePath}`);
  }

  const content = fs.readFileSync(absolutePath, "utf-8");
  const headerCommentMatch = content.match(/^(\/\*\*[\s\S]*?\*\/)/);
  const headerComment = headerCommentMatch ? headerCommentMatch[1] : undefined;

  let description: string | undefined;
  if (headerComment) {
    const descMatch = headerComment.match(/@description\s+(.+?)(?:\n|\*\/)/s);
    if (descMatch) {
      description = descMatch[1].trim();
    }
  }

  const { default: mockModule } = await import(
    `${absolutePath}?t=${Date.now()}`
  );

  const exportedConfig =
    typeof mockModule === "function" ? mockModule() : mockModule;
  const hasDynamicData = typeof exportedConfig?.data === "function";
  const config: MockFileConfig = {
    ...DEFAULT_CONFIG,
    ...(exportedConfig ?? {}),
  };

  const serializable = !hasDynamicData;
  const dataObj =
    typeof config.data === "object" && config.data !== null
      ? (config.data as Record<string, unknown>)
      : null;
  const isBinary = dataObj !== null && "__binaryFile" in dataObj;

  return {
    config,
    serializable,
    hasDynamicData,
    headerComment,
    description,
    dataText: isBinary
      ? `/* Binary file: ${dataObj?.__binaryFile} */`
      : serializable
        ? JSON.stringify(config.data ?? null, null, 2)
        : "/* data is generated dynamically and cannot be edited here */",
    isBinary,
  };
}

export async function writeMockFile(
  filePath: string,
  mockInfo: Pick<MockFileInfo, "config" | "headerComment" | "description">,
): Promise<void> {
  const absolutePath = resolveAbsolutePath(filePath);

  let header = mockInfo.headerComment || "";

  if (mockInfo.description !== undefined) {
    if (header) {
      if (/@description/.test(header)) {
        header = header.replace(
          /@description\s+.+?(?=\n|\*\/)/s,
          `@description ${mockInfo.description}`,
        );
      } else {
        header = header.replace(
          /\*\//,
          ` * @description ${mockInfo.description}\n */`,
        );
      }
    }
  }

  const content = header ? `${header}\n` : "";
  const finalContent = `${content}export default ${JSON.stringify(mockInfo.config, null, 2)}\n`;

  try {
    const formatted = await formatWithPrettier(finalContent);
    fs.writeFileSync(absolutePath, formatted, "utf-8");
  } catch {
    console.error("Error formatting code with prettier, writing raw content");
    fs.writeFileSync(absolutePath, finalContent, "utf-8");
  }
}
